import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  LockOutlined as LockOutlinedIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { api } from '../services/api';

const MIN_PASSWORD_LENGTH = 8;

const getResetTokenFromUrl = () => {
  try {
    return new URLSearchParams(window.location.search).get('resetToken') || '';
  } catch (error) {
    return '';
  }
};

const clearResetTokenFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('resetToken');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  } catch (error) {
    // Ignore history API issues in restricted browser contexts.
  }
};

function ForgotPasswordForm({ onBackToLogin }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.forgotPassword();
      setSent(true);
    } catch (submitError) {
      const message = submitError?.response?.data?.error || 'Could not send reset email. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Stack spacing={3}>
        <Alert severity="success">
          If password reset is configured for this console, an email with a reset link has been sent.
        </Alert>
        <Button variant="outlined" onClick={onBackToLogin} fullWidth>
          Back to sign in
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} component="form" onSubmit={handleSubmit}>
      <Stack spacing={1}>
        <Typography variant="h5">Reset your password</Typography>
        <Typography variant="body2" color="text.secondary">
          We&apos;ll email a reset link to the administrator address configured for this console.
        </Typography>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Button type="submit" variant="contained" disabled={loading} size="large" fullWidth>
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Send reset link'}
      </Button>
      <Link component="button" type="button" variant="body2" onClick={onBackToLogin} underline="hover">
        Back to sign in
      </Link>
    </Stack>
  );
}

function ResetPasswordForm({ token, onResetComplete }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.resetPassword({ token, newPassword });
      onResetComplete();
    } catch (submitError) {
      const message = submitError?.response?.data?.error || 'Could not reset password. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3} component="form" onSubmit={handleSubmit}>
      <Stack spacing={1}>
        <Typography variant="h5">Choose a new password</Typography>
        <Typography variant="body2" color="text.secondary">
          Enter a new password for the admin console.
        </Typography>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <TextField
        label="New password"
        fullWidth
        type={passwordVisible ? 'text' : 'password'}
        value={newPassword}
        onChange={(event) => setNewPassword(event.target.value)}
        autoComplete="new-password"
        disabled={loading}
        required
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={passwordVisible ? 'hide password' : 'show password'}
                onClick={() => setPasswordVisible((visible) => !visible)}
                edge="end"
              >
                {passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        label="Confirm new password"
        fullWidth
        type={passwordVisible ? 'text' : 'password'}
        value={confirmPassword}
        onChange={(event) => setConfirmPassword(event.target.value)}
        autoComplete="new-password"
        disabled={loading}
        required
      />

      <Button type="submit" variant="contained" disabled={loading} size="large" fullWidth>
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Reset password'}
      </Button>
    </Stack>
  );
}

export default function LoginScreen({
  onLogin,
  loading = false,
  message = '',
  themeMode = 'dark',
  onToggleThemeMode,
}) {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const initialResetToken = getResetTokenFromUrl();
  const [view, setView] = useState(initialResetToken ? 'reset' : 'login');
  const [resetToken] = useState(initialResetToken);
  const [resetSuccessMessage, setResetSuccessMessage] = useState('');

  const goToLogin = () => {
    clearResetTokenFromUrl();
    setResetSuccessMessage('');
    setView('login');
  };

  const handleResetComplete = () => {
    clearResetTokenFromUrl();
    setResetSuccessMessage('Password reset. Please sign in with your new password.');
    setView('login');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Username and password are required.');
      return;
    }

    try {
      await onLogin({ username, password });
    } catch (submitError) {
      const message = submitError?.response?.data?.error || 'Login failed. Please try again.';
      setError(message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        bgcolor: 'background.default',
      }}
    >
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, md: 4 },
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'rgba(255,255,255,0.08)',
          bgcolor: '#0B1120',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Scammer Detection"
            sx={{
              height: 48,
              width: 'auto',
              objectFit: 'contain',
            }}
          />
          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
            Admin Console
          </Typography>
        </Stack>

        {onToggleThemeMode && (
          <Tooltip title={themeMode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
            <IconButton
              aria-label={themeMode === 'dark' ? 'switch to light theme' : 'switch to dark theme'}
              onClick={onToggleThemeMode}
              sx={{ color: '#F8FAFC' }}
            >
              {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 0.9fr) minmax(380px, 1fr)' },
          alignItems: 'center',
          gap: { xs: 3, md: 5 },
          width: '100%',
          maxWidth: 1120,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: { xs: 'none', md: 'block' },
            p: 4,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: themeMode === 'dark' ? '#0B1120' : '#F8FAFC',
            color: themeMode === 'dark' ? '#F8FAFC' : 'text.primary',
          }}
        >
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" sx={{ color: 'primary.light', fontWeight: 700 }}>
                Enterprise Access
              </Typography>
              <Typography variant="h4" sx={{ mt: 0.5 }}>
                Secure operations for telecom risk control.
              </Typography>
            </Box>
            <Divider />
            <Stack spacing={2}>
              {[
                'Centralized fraud and routing oversight',
                'AI1 scammer screening and AI2 translation visibility',
                'Operational health, call history, and trace review',
              ].map((item) => (
                <Stack key={item} direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      flex: '0 0 auto',
                    }}
                  />
                  <Typography variant="body2" color={themeMode === 'dark' ? '#CBD5E1' : 'text.secondary'}>
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 440,
            justifySelf: { xs: 'center', md: 'end' },
            p: { xs: 3, sm: 4 },
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {view === 'forgot' ? (
            <ForgotPasswordForm onBackToLogin={goToLogin} />
          ) : view === 'reset' ? (
            <ResetPasswordForm token={resetToken} onResetComplete={handleResetComplete} />
          ) : (
            <Stack spacing={3} component="form" onSubmit={handleSubmit}>
              <Stack spacing={1}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 1,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    mb: 1,
                  }}
                >
                  <LockOutlinedIcon fontSize="small" />
                </Box>
                <Typography variant="h5">Sign in to admin console</Typography>
                <Typography variant="body2" color="text.secondary">
                  Use your administrator credentials to continue.
                </Typography>
              </Stack>

              {resetSuccessMessage ? <Alert severity="success">{resetSuccessMessage}</Alert> : null}
              {message ? <Alert severity="info">{message}</Alert> : null}
              {error ? <Alert severity="error">{error}</Alert> : null}

              <TextField
                label="Username"
                fullWidth
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                disabled={loading}
                required
              />

              <TextField
                label="Password"
                fullWidth
                type={passwordVisible ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                disabled={loading}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={passwordVisible ? 'hide password' : 'show password'}
                        onClick={() => setPasswordVisible((visible) => !visible)}
                        edge="end"
                      >
                        {passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" justifyContent="flex-end">
                <Link component="button" type="button" variant="body2" onClick={() => setView('forgot')} underline="hover">
                  Forgot password?
                </Link>
              </Stack>

              <Button type="submit" variant="contained" disabled={loading} size="large" fullWidth>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
              </Button>
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
